/// <reference path="interfaces.ts" />

/**
 * Viewer-side global filter pane. Reads the dashboard's global filter definitions. Edits stay
 * local until Apply (saving on data calls); Apply commits the controls into the pane's
 * in-memory applied state, serialises it into the URL query as shareable deep-link params,
 * and publishes a data refresh so the widgets refetch; a page load parses those params back
 * into the applied state before the first fetch and pre-expands the pane. The pane is an
 * in-canvas panel anchored below the titlebar (the toggle is part of the titlebar's rendering
 * when one exists, or sits in a reserved top strip on dashboards without a titlebar) that
 * expands to fit its contents; when the pane's region would overlap widgets they are ALL
 * pushed in from the side to make room, while a pane that fits over open space leaves the
 * canvas untouched.
 */
namespace distill.dashboards.globalFilterPane {
    class GlobalFilterPaneInitArgs {
        design!: distill.dashboards.IDashboardDesign;
        fetchGlobalFilterOptions!: (filter: distill.dashboards.IDashboardGlobalFilter, search?: string) => Promise<distill.dashboards.IGlobalFilterOption[]>;
    }

    const PANE_SELECTOR = '#dashboard-global-filter-pane';
    const TOGGLE_SELECTOR = '#dashboard-global-filter-toggle';
    const BAR_SELECTOR = '#dashboard-global-filter-bar';
    // Height of the reserve strip used when the dashboard has NO titlebar at all — compact
    // (button + breathing room) since there is no titlebar extent to match. With a titlebar
    // the space is preserved by the titlebar component itself instead.
    const BAR_HEIGHT_PX = 48;
    const BODY_SELECTOR = '.dashboard-global-filter-body';
    const EMPTY_VALUE = '';
    // The pane's component box width — the CSS width in PublicView.scss (270px card + the
    // component gutter padding). The squeeze reserve adds the pane's dynamic right inset,
    // leaving the standard 16px widget gutter to the nearest squeezed widget card.
    const PANE_WIDTH_PX = 286;
    // Each widget card is inset 0.5rem inside its gridstack item; the squeeze reserve
    // positions the ITEM, so this is subtracted to make the card-to-pane gutter match the
    // card-to-edge gutter seen when the pane is closed.
    const WIDGET_CARD_PADDING_PX = 8;
    const DATE_PRESET_ALL = 'All';
    const DATE_PRESET_CUSTOM = 'CustomRange';
    // Presets whose resolved date range can be inferred from the applied values (All/CustomRange are handled separately).
    const INFERABLE_DATE_PRESETS = ['Today', 'Last7Days', 'ThisMonth', 'LastMonth', 'Last6Months', 'Last12Months'];

    // The dropdown's applied value pipe-joins the selection set (an empty segment is the
    // blank option), so literal pipes in values are percent-escaped to keep the join
    // unambiguous ('%' first, so the escape itself round-trips).
    const escapePipe = (value: string): string => value.replace(/%/g, '%25').replace(/\|/g, '%7C');
    const unescapePipe = (value: string): string => value.replace(/%7C/gi, '|').replace(/%25/g, '%');

    let _filters: distill.dashboards.IDashboardGlobalFilter[] = [];
    let _fetchGlobalFilterOptions: GlobalFilterPaneInitArgs['fetchGlobalFilterOptions'];
    // Applied filter values, keyed by filter id (range suffixes _gte/_lte on the same key
    // space) plus the serialized committed state for the Apply button's dirty comparison.
    // These internal keys never reach the URL — commits serialise to dataName-keyed params
    // (writeUrlParams) and loads parse them back (readUrlParams).
    let _appliedParams = new URLSearchParams();
    let _lastCommittedParams = '';
    let _squeezed: HTMLElement[] = [];
    let _paneCloseTimer: number | null = null;

    // Matches gridstack's own item animation (gridstack.css: left/top/height/width 0.3s ease),
    // so the pane slides in/out of view at the same speed the widgets are pushed/released.
    const PANE_ANIMATION_MS = 300;

    // Option-list caching and the selection store live in filterOptionsStore (shared with the
    // table widget's column filter popovers so both behave identically). Keys are namespaced
    // by store; the selection is seeded from the applied values the first time a filter's
    // dropdown is populated and from then on follows the user's checks.
    function selectionsKey(filter: distill.dashboards.IDashboardGlobalFilter): string {
        return `globalFilter:${filter.id}`;
    }

    function getSelections(filter: distill.dashboards.IDashboardGlobalFilter): Map<string, distill.dashboards.filterOptionsStore.ISelectionEntry> {
        return distill.dashboards.filterOptionsStore.getSelections(selectionsKey(filter));
    }

    /** Suffixes a filter contributes to the URL param space ('' for single-value types). */
    function paramSuffixes(filter: distill.dashboards.IDashboardGlobalFilter): string[] {
        return filter.type === distill.dashboards.DashboardGlobalFilterType.DateRange || filter.type === distill.dashboards.DashboardGlobalFilterType.NumberRange
            ? ['_gte', '_lte']
            : [''];
    }

    /**
     * The URL parameter name for a filter. An explicit designer-managed Data Name wins — that's
     * the point of the field: renaming the label or reusing it cannot break or collide share
     * links. Otherwise the name derives from the label (words camel-cased after stripping
     * whitespace and other characters), so shared links read like
     * `?country=France&dateRange_gte=2026-01-01`; falls back to the filter id when the label is
     * blank or strips to nothing. The label-derived form has two caveats: a label rename
     * changes the param name, and two same-named labels share a param (last applied wins).
     */
    function filterDataName(filter: distill.dashboards.IDashboardGlobalFilter): string {
        const explicitName = (filter.dataName || '').trim();
        if (explicitName)
            return explicitName;

        const name = (filter.label || '')
            .replace(/[^a-zA-Z0-9 ]/g, '')
            .split(' ')
            .filter(w => !!w)
            .map((w, i) => i === 0 ? w.toLowerCase() : (w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()))
            .join('');

        return name || filter.id;
    }

    /** Reads the pane's params out of the current URL query, re-keyed into the filter-id space. */
    function readUrlParams(): URLSearchParams {
        const url = new URLSearchParams(window.location.search);
        const state = new URLSearchParams();
        _filters.forEach(filter => {
            const name = filterDataName(filter);
            paramSuffixes(filter).forEach(suffix => {
                const value = url.get(`${name}${suffix}`);
                if (value !== null)
                    state.set(`${filter.id}${suffix}`, value);
            });
        });

        return state;
    }

    /** Serialises the applied state into the URL query (replaceState — no history entry); other query params are preserved. */
    function writeUrlParams(): void {
        const url = new URLSearchParams(window.location.search);
        _filters.forEach(filter => {
            const name = filterDataName(filter);
            paramSuffixes(filter).forEach(suffix => {
                url.delete(`${name}${suffix}`);
                const key = `${filter.id}${suffix}`;
                // has() (not a truthy check): an empty value is the dropdown's blank-only
                // selection — a real filter — so it serialises as `name=` and survives.
                if (_appliedParams.has(key)) {
                    const value = _appliedParams.get(key) || '';
                    url.set(`${name}${suffix}`, value);
                }
            });
        });

        const query = url.toString();
        history.replaceState(null, '', query ? `${window.location.pathname}?${query}` : window.location.pathname);
    }

    export function init(args: GlobalFilterPaneInitArgs): void {
        _filters = (args.design && args.design.globalFilters) ? args.design.globalFilters : [];
        _fetchGlobalFilterOptions = args.fetchGlobalFilterOptions;

        const $pane = $(PANE_SELECTOR);
        const $toggle = $(TOGGLE_SELECTOR);
        if (!$pane.length)
            return;

        if (_filters.length === 0) {
            $pane.addClass('d-none');
            $toggle.addClass('d-none');
            return;
        }

        $toggle.removeClass('d-none');
        // Deep links: parse the URL's filter params into the applied state before anything
        // builds from it, so controls prefill and the first fetch (published after init) is already filtered.
        _appliedParams = readUrlParams();
        // Seed the committed-state baseline so the Apply button only lights up when the
        // controls actually differ from the applied values.
        _lastCommittedParams = _appliedParams.toString();
        buildControls($pane.find(BODY_SELECTOR));
        bindEvents($pane, $toggle);
        // A link carrying filter params lands pre-filtered — expand the pane so the viewer
        // sees the controls behind the data (an unfiltered URL stays collapsed).
        if (_appliedParams.toString() !== '')
            openPane($pane, $toggle);
        updateToggleState();
        positionToggle();
    }

    /** Returns the applied filter values, keyed by filter id (range suffixes _gte/_lte on the same key space). */
    export function getAppliedGlobalFilterValues(): { [key: string]: string } {
        const values: { [key: string]: string } = {};
        _appliedParams.forEach((value, key) => values[key] = value);

        return values;
    }

    /**
     * Expands the applied values into filter selections — the wire format for data
     * fetches. The in-memory encoding (filter-id keys, pipe-joined multi-selects, _gte/_lte
     * range suffixes) exists only in the browser; the server only sees structured selections.
     */
    export function getAppliedFilters(): IDashFilterSelection[] {
        const values = getAppliedGlobalFilterValues();
        const filters: IDashFilterSelection[] = [];

        _filters.forEach(filter => {
            switch (filter.type) {
                case distill.dashboards.DashboardGlobalFilterType.Text: {
                    const value = values[filter.id];
                    if (value)
                        filters.push({ filterId: filter.id, values: [value] });
                    break;
                }
                case distill.dashboards.DashboardGlobalFilterType.Dropdown: {
                    // The applied value is the pipe-joined selections; an empty segment is the
                    // blank option (the query side treats an empty entry as IsBlank), so the
                    // split is kept intact — including a lone '' for a blank-only selection.
                    if (values[filter.id] !== undefined)
                        filters.push({ filterId: filter.id, values: values[filter.id].split('|').map(unescapePipe) });
                    break;
                }
                case distill.dashboards.DashboardGlobalFilterType.DateRange:
                case distill.dashboards.DashboardGlobalFilterType.NumberRange: {
                    const from = values[`${filter.id}_gte`];
                    const to = values[`${filter.id}_lte`];
                    if (from || to)
                        filters.push({ filterId: filter.id, from: from || '', to: to || '' });
                    break;
                }
            }
        });

        return filters;
    }

    function bindEvents($pane: JQuery, $toggle: JQuery): void {
        $toggle.on('click', (e) => {
            e.stopPropagation();
            e.preventDefault();
            togglePane($pane, $toggle);
        });

        // Reposition the toggle, and re-evaluate the pane/squeeze when the titlebar height
        // changes (window resize recalculates cell heights).
        $(window).on('resize.dashboard-global-filter', () => {
            positionToggle();

            if (!$pane.hasClass('d-none') && !$pane.hasClass('pane-off-canvas')) {
                positionPane($pane, $toggle);
                updatePaneSqueeze($pane);
            }
        });

        // Edits stay local until Apply (saves on data calls); Reset clears and applies at once.
        // The pane is only toggled by the titlebar's filter button — no outside-click closing.
        $pane.find('.dashboard-global-filter-apply').on('click', () => applyFilters());
        $pane.find('.dashboard-global-filter-reset').on('click', (e) => {
            e.preventDefault();
            resetFilters();
        });

        // Any control edit updates the Apply button's pending state (select2 fires change too).
        $pane.on('change input', 'input, select', () => updateApplyState());
        updateApplyState();
    }

    function togglePane($pane: JQuery, $toggle: JQuery): void {
        if ($pane.hasClass('d-none') || $pane.hasClass('pane-off-canvas'))
            openPane($pane, $toggle);
        else
            closePane($pane);

        updateToggleState();
    }

    /** Toggle chrome: solid/primary when expanded with active filters; outline when collapsed,
     *  secondary when cleared. A pane mid-close (sliding off-canvas, not yet hidden) counts as collapsed. */
    function updateToggleState(): void {
        const $toggle = $(TOGGLE_SELECTOR);
        if (!$toggle.length)
            return;

        const $pane = $(PANE_SELECTOR);
        const expanded = !$pane.hasClass('d-none') && !$pane.hasClass('pane-off-canvas');
        const active = Object.values(getAppliedGlobalFilterValues()).some(v => v !== EMPTY_VALUE);

        $toggle.toggleClass('btn-primary', expanded && active);
        $toggle.toggleClass('btn-outline-primary', !expanded && active);
        $toggle.toggleClass('btn-secondary', expanded && !active);
        $toggle.toggleClass('btn-outline-secondary', !expanded && !active);
    }

    function openPane($pane: JQuery, $toggle: JQuery): void {
        if (_paneCloseTimer !== null)
            window.clearTimeout(_paneCloseTimer);
        _paneCloseTimer = null;

        // Start off-canvas, then release after a style flush — the pane transitions in from
        // the right over PANE_ANIMATION_MS while the widgets' squeeze pushes them aside over
        // the same gridstack animation.
        $pane.addClass('pane-off-canvas');
        $pane.removeClass('d-none');
        positionPane($pane, $toggle);
        updatePaneSqueeze($pane);
        void $pane[0].offsetWidth;
        $pane.removeClass('pane-off-canvas');

        // Select2 needs visible elements to size correctly, so initialise on first open
        // (initForDesign skips selects that are already initialised).
        appn.select.initForDesign({
            elements: $pane.find(BODY_SELECTOR).find('select'),
            minimumResultsForSearch: '10'
        });
    }

    function closePane($pane: JQuery): void {
        // Slide out while the widgets animate back into place; hide only once both settle.
        if (_paneCloseTimer !== null)
            window.clearTimeout(_paneCloseTimer);
        $pane.addClass('pane-off-canvas');
        clearPaneSqueeze();
        _paneCloseTimer = window.setTimeout(() => {
            _paneCloseTimer = null;
            $pane.addClass('d-none');
        }, PANE_ANIMATION_MS);
    }

    /**
     * Keeps the toggle at the top: with a titlebar component it is adopted beside the card by
     * titleBarFn (positioned by CSS), so this only makes sure it lives there; without one it
     * sits in the reserve strip — a compact button-height reservation that pushes the widgets
     * down. Without filters both stay hidden and the widgets reclaim the space.
     */
    function positionToggle(): void {
        const $toggle = $(TOGGLE_SELECTOR);
        const $bar = $(BAR_SELECTOR);
        if (!$toggle.length || !$bar.length)
            return;

        const $titlebar = $('.component.titleBar').first();

        if ($titlebar.length) {
            $bar.addClass('d-none');
            if ($toggle.parent()[0] !== $titlebar[0])
                $titlebar.append($toggle);

            $toggle.css({ position: '', top: '', left: '', right: '' });
            return;
        }

        $bar.removeClass('d-none').height(BAR_HEIGHT_PX);
        if ($toggle.parent()[0] !== $bar[0])
            $bar.append($toggle);

        $toggle.css({ position: '', top: '', left: '', right: '' });
    }

    function positionPane($pane: JQuery, $toggle: JQuery): void {
        // In-canvas positioning (absolute, scrolls with the page): the pane hangs below the
        // titlebar row (a hidden title still reserves its space via visibility), or below the
        // toggle in the reserve strip on dashboards with no titlebar. Its right edge aligns to
        // the toggle's 0.5rem right gutter, which the widgets' squeeze reserve accounts for.
        const $content = $('#content.dash-view');
        const $titlebar = $('.component.titleBar').filter(':visible').first();
        const topAnchor = $titlebar.length ? $titlebar : $toggle;
        const topAnchorOffset = topAnchor.length ? topAnchor.offset() : null;
        const toggleOffset = $toggle.offset();
        const contentOffset = $content.length ? $content.offset() : null;
        if (!topAnchorOffset || !toggleOffset || !contentOffset)
            return;

        // 0.5rem (8px) floor so the pane never hugs the viewport edge; +9 nudges past the
        // titlebar's bottom border.
        const rightInset = Math.max(8, contentOffset.left + ($content.outerWidth() || 0)
            - (toggleOffset.left + ($toggle.outerWidth() || 0)));

        $pane.css({
            top: topAnchorOffset.top + (topAnchor.outerHeight() || 0) - contentOffset.top + 9,
            right: rightInset
        });
    }

    /**
     * Makes room for the pane only when a widget is actually in its way: when nothing
     * overlaps, the pane floats over open space and the canvas keeps its full width; when any
     * widget overlaps, ALL widgets push in from the side (a partial push would leave the grid
     * misaligned) — gridstack positions items with percentages, so the left/width overrides
     * keep working on resize. The titlebar is excluded and keeps its narrowed width. The
     * reserve subtracts each widget's own card padding so the card-to-pane gutter matches the
     * card-to-edge gutter of the closed state.
     */
    function updatePaneSqueeze($pane: JQuery): void {
        clearPaneSqueeze();

        const paneEl = $pane[0];
        const $content = $('#content.dash-view');
        if (!paneEl || !$content.length)
            return;

        const rightInset = parseFloat($pane.css('right')) || 0;
        const reserve = PANE_WIDTH_PX + rightInset - WIDGET_CARD_PADDING_PX;

        // The pane's viewport box: absolutely positioned inside #content, right-aligned to
        // its inset. offsetWidth/offsetHeight ignore the slide-in transform.
        const contentRect = $content[0].getBoundingClientRect();
        const paneWidth = paneEl.offsetWidth || PANE_WIDTH_PX;
        const paneTop = contentRect.top + (parseFloat($pane.css('top')) || 0);
        const paneBottom = paneTop + paneEl.offsetHeight;
        const paneLeft = contentRect.right - rightInset - paneWidth;

        const widgets = $('.grid-stack > .component').not('.titleBar').toArray() as HTMLElement[];

        // A widget overlaps when it reaches into the pane's band and its rows intersect the
        // pane's vertical extent. Horizontally the grid attributes decide (the rendered width
        // may be mid-animation when a previous squeeze is releasing); vertically the rendered
        // box (the squeeze only alters left/width, so top/height are always current).
        const overlapsPane = (el: HTMLElement): boolean => {
            const $el = $(el);
            const rect = el.getBoundingClientRect();
            if (rect.bottom <= paneTop || rect.top >= paneBottom)
                return false;

            const grid = el.closest('.grid-stack');
            const gridWidth = grid ? grid.clientWidth : contentRect.width;
            const xFrac = (Number($el.attr('gs-x')) || 0) / 12;
            const wFrac = (Number($el.attr('gs-w')) || 1) / 12;
            const rightEdge = (grid ? grid.getBoundingClientRect().left : contentRect.left) + (xFrac + wFrac) * gridWidth;

            return rightEdge > paneLeft;
        };

        // Open space: nothing in the pane's band — leave the canvas alone.
        if (!widgets.some(overlapsPane))
            return;

        widgets.forEach(el => {
            const $el = $(el);

            // Fraction of the 12-column grid this item covers.
            const xFrac = (Number($el.attr('gs-x')) || 0) / 12;
            const wFrac = (Number($el.attr('gs-w')) || 1) / 12;

            $el.css({
                left: `calc((100% - ${reserve}px) * ${xFrac})`,
                width: `calc((100% - ${reserve}px) * ${wFrac})`
            });
            _squeezed.push(el);
        });
    }

    function clearPaneSqueeze(): void {
        _squeezed.forEach(el => $(el).css({ left: '', width: '' }));
        _squeezed = [];
    }

    function buildControls($body: JQuery): void {
        $body.empty();
        if (!$body.length)
            return;

        const params = _appliedParams;
        _filters.forEach(filter => $body.append(buildFilterControl(filter, params)));

        // Apply sits below the filters, right-aligned.
        $body.append(appn.utils.cloneTemplate('global-filter-apply-row'));

        // Initialise date pickers for any custom From/To inputs. The picker fires only
        // dp.change — the afterChanged callback routes picker selections into the same
        // Apply-state update as typed edits.
        appn.ui.datePicker($body.find('.datepicker'), () => updateApplyState(), 'YYYY-MM-DD');

        _filters.forEach(filter => {
            if (filter.type !== distill.dashboards.DashboardGlobalFilterType.Dropdown)
                return;

            populateDropdown($body.find(`.dashboard-global-filter-item[data-filter-id="${filter.id}"]`), filter);
        });
    }

    function buildFilterControl(filter: distill.dashboards.IDashboardGlobalFilter, params: URLSearchParams): JQuery {
        const $item = appn.utils.cloneTemplate('global-filter-control')
            .attr('data-filter-id', filter.id)
            .attr('data-filter-type', filter.type);
        $item.find('.dashboard-global-filter-item-label').text(filter.label || '');

        // The template is a superset — show the section matching the filter type, hide the rest.
        switch (filter.type) {
            case distill.dashboards.DashboardGlobalFilterType.DateRange: {
                const $section = $item.find('.dashboard-global-filter-date-section').removeClass('d-none');
                const $preset = $section.find('.dashboard-global-filter-date-preset');
                getDatePresetOptions().forEach(preset => $preset.append($('<option>').val(preset.value).text(preset.label)));

                const fromValue = params.get(`${filter.id}_gte`) || EMPTY_VALUE;
                const toValue = params.get(`${filter.id}_lte`) || EMPTY_VALUE;
                const inferredPreset = inferDatePreset(fromValue, toValue);

                $preset.val(inferredPreset);

                const $custom = $section.find('.dashboard-global-filter-custom-dates');
                $custom.find('.dashboard-global-filter-from').val(fromValue);
                $custom.find('.dashboard-global-filter-to').val(toValue);
                $custom.toggleClass('d-none', inferredPreset !== DATE_PRESET_CUSTOM);

                $preset.on('change', () => {
                    $custom.toggleClass('d-none', $preset.val() !== DATE_PRESET_CUSTOM);
                    const $pane = $(PANE_SELECTOR);
                    positionPane($pane, $(TOGGLE_SELECTOR));
                    updatePaneSqueeze($pane);
                });
                break;
            }
            case distill.dashboards.DashboardGlobalFilterType.Dropdown:
                // Multi-select with search, matching the table widget's column filter values
                // list: the search narrows the options, (Select All) toggles the visible ones,
                // and the applied selection is pipe-joined into the filter-id param.
                $item.find('.dashboard-global-filter-dropdown-section').removeClass('d-none');
                break;
            case distill.dashboards.DashboardGlobalFilterType.NumberRange: {
                const $section = $item.find('.dashboard-global-filter-number-section').removeClass('d-none');
                $section.find('.dashboard-global-filter-number-from').val(params.get(`${filter.id}_gte`) || EMPTY_VALUE);
                $section.find('.dashboard-global-filter-number-to').val(params.get(`${filter.id}_lte`) || EMPTY_VALUE);
                break;
            }
            default:
                $item.find('.dashboard-global-filter-text-section').removeClass('d-none')
                    .find('.dashboard-global-filter-text').val(params.get(filter.id) || EMPTY_VALUE);
        }

        return $item;
    }

    function populateDropdown($item: JQuery, filter: distill.dashboards.IDashboardGlobalFilter): void {
        const $values = $item.find('.dashboard-global-filter-values');
        const $search = $item.find('.dashboard-global-filter-dropdown-search');
        const $searchClear = $item.find('.dashboard-global-filter-search-clear');
        const $selectAll = $item.find('.dashboard-global-filter-select-all');
        const $limited = $item.find('.dashboard-global-filter-limited');
        if (!$values.length)
            return;

        // Bindings are replaced rather than accumulated.
        let searchToken = 0;
        // Whether the data has blanks, remembered from the unsearched render — a search can never match one.
        let hasBlanks = false;

        // A stale search term has no meaning in the fresh context — reset it.
        $search.val('');
        $searchClear.toggle(false);

        // Seed the selection set from the applied values the first time this dropdown is
        // populated; from then on the set follows the user's checks. Seeded labels fall back
        // to the raw value until an option list provides the display text.
        if (!distill.dashboards.filterOptionsStore.hasSelections(selectionsKey(filter))) {
            // An empty segment is the blank option, so the split is kept intact; no param at all seeds nothing (All).
            const applied = _appliedParams.has(filter.id) ? (_appliedParams.get(filter.id) || '').split('|').map(unescapePipe) : [];
            distill.dashboards.filterOptionsStore.seedSelections(selectionsKey(filter), applied);
        }

        const renderOptions = (options: distill.dashboards.filterOptionsStore.IFilterValueOption[], search: string): void => {
            // A search filters what is SHOWN but never mutates the selection: checkboxes
            // render from the selection set, so a value checked under one search stays
            // checked when it is cleared or replaced. Unsearched renders also pin every
            // selection a truncated list dropped.
            const selections = getSelections(filter);

            // The blank is a row like any other: the server reports it as an empty value.
            const blankOption = options.find(o => o.value === '') || null;
            const valueOptions = options.filter(o => o.value !== '');

            // Refresh the remembered labels for selections the current list contains.
            valueOptions.forEach(o => {
                if (selections.has(o.value))
                    selections.set(o.value, { display: o.display || o.value, count: 0 });
            });

            $values.empty();

            // The blank is pinned above the values: picked alone it filters to blanks, picked with values it adds them.
            if (search === '')
                hasBlanks = blankOption !== null;
            if (hasBlanks || selections.has(''))
                $values.append(buildDropdownValueOption('', '', selections.has('')));

            if (search === '') {
                const known = new Set(valueOptions.map(o => o.value));
                selections.forEach((prior, value) => {
                    if (!known.has(value) && value !== '')
                        $values.append(buildDropdownValueOption(value, prior.display, true));
                });
            }
            valueOptions.forEach(o => {
                $values.append(buildDropdownValueOption(o.value, o.display || o.value, selections.has(o.value)));
            });

            // Same cap hint as the table widget's column filter — the list was truncated.
            $limited.toggleClass('d-none', valueOptions.length < distill.dashboards.FILTER_OPTIONS_LIMIT);

            syncDropdownSelectAll($item);
        };

        const loadOptions = (search: string): void => {
            const token = ++searchToken;

            // The option list is the filter's own source dataset, unaffected by the other applied filters, so the cache key
            // is the filter alone. Truncated lists only serve the unsearched render — searches keep going to the server.
            const cacheKey = selectionsKey(filter);
            const cached = distill.dashboards.filterOptionsStore.getCached(cacheKey, search);
            if (cached !== null) {
                if (token !== searchToken)
                    return;

                renderOptions(cached, search);
                return;
            }

            _fetchGlobalFilterOptions(filter, search).then(options => {
                if (token !== searchToken || !$values.length)
                    return;

                if (search === '')
                    distill.dashboards.filterOptionsStore.setCached(cacheKey, options);

                renderOptions(options, search);
            });
        };

        $search.off('input').on('input', () => {
            const term = (($search.val() as string) || '').trim();
            $searchClear.toggle(term.length > 0);
            window.clearTimeout($search.data('searchTimer') as number);
            $search.data('searchTimer', window.setTimeout(() => loadOptions(term), 300));
        });

        $searchClear.off('click').on('click', () => {
            $search.val('').trigger('input').trigger('focus');
        });

        // Every checkbox interaction reconciles the selection set: rendered values are
        // added/removed per their checked state (remembering their display label);
        // values not currently rendered are untouched and keep their stored state.
        const syncSelections = (): void => {
            const selections = getSelections(filter);
            $values.find('.dashboard-global-filter-value').each((_, el) => {
                const $cb = $(el).find('input');
                const value = String($cb.val());
                if ($cb.prop('checked')) {
                    selections.set(value, {
                        display: $(el).find('.dashboard-global-filter-value-label').text(),
                        count: 0
                    });
                } else
                    selections.delete(value);
            });
            syncDropdownSelectAll($item);
        };

        $selectAll.off('change').on('change', 'input', e => {
            const checked = $(e.target).prop('checked');
            $values.children().find('input').prop('checked', checked);
            syncSelections();
        });

        $values.off('change').on('change', 'input', syncSelections);

        loadOptions('');
    }

    function buildDropdownValueOption(value: string, display: string, isChecked: boolean): JQuery {
        // The blank arrives as an empty value — render it with a translated label; the checkbox carries it end to end.
        const label = value === '' ? T('(blank)') : (display || value);
        return $('<label class="dashboard-global-filter-value"></label>')
            .append($('<input type="checkbox" class="dashboard-global-filter-value-cb" />').val(value).prop('checked', isChecked))
            .append($('<span class="dashboard-global-filter-value-label"></span>').text(label));
    }

    function syncDropdownSelectAll($item: JQuery): void {
        const $checks = $item.find('.dashboard-global-filter-value input');
        const total = $checks.length;
        const checked = $checks.filter(':checked').length;
        $item.find('.dashboard-global-filter-select-all-cb').prop('checked', total > 0 && checked === total).prop('indeterminate', checked > 0 && checked < total);
    }

    function applyFilters(): void {
        commitFilters(collectFilterParams());
        updateApplyState();
        updateToggleState();
    }

    function collectFilterParams(): URLSearchParams {
        const params = new URLSearchParams();
        const $body = $(PANE_SELECTOR).find(BODY_SELECTOR);

        _filters.forEach(filter => {
            const $item = $body.find(`.dashboard-global-filter-item[data-filter-id="${filter.id}"]`);

            switch (filter.type) {
                case distill.dashboards.DashboardGlobalFilterType.DateRange: {
                    const preset = ($item.find('.dashboard-global-filter-date-preset').val() as string) || 'All';
                    let from = EMPTY_VALUE;
                    let to = EMPTY_VALUE;

                    if (preset === 'CustomRange') {
                        from = (($item.find('.dashboard-global-filter-from').val() as string) || '').trim();
                        to = (($item.find('.dashboard-global-filter-to').val() as string) || '').trim();
                    }
                    else {
                        const range = resolveDatePreset(preset);
                        from = range.from || EMPTY_VALUE;
                        to = range.to || EMPTY_VALUE;
                    }

                    params.set(`${filter.id}_gte`, from);
                    params.set(`${filter.id}_lte`, to);
                    break;
                }
                case distill.dashboards.DashboardGlobalFilterType.NumberRange: {
                    const from = (($item.find('.dashboard-global-filter-number-from').val() as string) || '').trim();
                    const to = (($item.find('.dashboard-global-filter-number-to').val() as string) || '').trim();
                    params.set(`${filter.id}_gte`, from);
                    params.set(`${filter.id}_lte`, to);
                    break;
                }
                case distill.dashboards.DashboardGlobalFilterType.Dropdown: {
                    // The selection set is the source of truth (including values hidden by a
                    // search or a truncated list). No selection means All; blank-only joins to
                    // the empty string — a real filter, kept by resolveApplied.
                    const selections = getSelections(filter);
                    if (selections.size > 0)
                        params.set(filter.id, Array.from(selections.keys()).map(escapePipe).join('|'));
                    break;
                }
                default: {
                    const value = (($item.find('.dashboard-global-filter-text').val() as string) || '');
                    params.set(filter.id, value);
                    break;
                }
            }
        });

        return params;
    }

    function resetFilters(): void {
        // There is no reload to clear the controls for us, so clear them directly, then commit.
        // commitFilters drops empty params from the applied state entirely.
        const $body = $(PANE_SELECTOR).find(BODY_SELECTOR);
        $body.find('.dashboard-global-filter-text, .dashboard-global-filter-from, .dashboard-global-filter-to, .dashboard-global-filter-number-from, .dashboard-global-filter-number-to, .dashboard-global-filter-dropdown-search').val('');
        $body.find('.dashboard-global-filter-value-cb').prop('checked', false);
        $body.find('.dashboard-global-filter-select-all-cb').prop('checked', false).prop('indeterminate', false);
        $body.find('.dashboard-global-filter-date-preset').val(DATE_PRESET_ALL);
        $body.find('.dashboard-global-filter-custom-dates').addClass('d-none');

        // Drop the dropdown selection sets too (checkbox clearing does not fire change events) —
        // the next interaction re-seeds them from the (now cleared) applied values.
        _filters.forEach(filter => distill.dashboards.filterOptionsStore.deleteSelections(selectionsKey(filter)));

        // The date-preset's change handler also re-hides the custom date inputs.
        $body.find('select').trigger('change');

        commitFilters(collectFilterParams());
        updateApplyState();
        updateToggleState();
    }

    /** Drops the EMPTY_VALUE sentinels ("no filter") from the resolved params. A dropdown's
     *  own key is the exception: an empty value there is the blank-only selection — a real
     *  filter (IsBlank server-side) — so it survives the cleanup. */
    function resolveApplied(params: URLSearchParams): URLSearchParams {
        const cleaned = new URLSearchParams();
        params.forEach((value, key) => {
            const isDropdownKey = _filters.some(f => f.id === key && f.type === distill.dashboards.DashboardGlobalFilterType.Dropdown);
            if (value !== EMPTY_VALUE || isDropdownKey)
                cleaned.set(key, value);
        });

        return cleaned;
    }

    /** Toggles the Apply button between neutral and primary (pending changes to commit). */
    function updateApplyState(): void {
        const $apply = $(PANE_SELECTOR).find('.dashboard-global-filter-apply');
        if (!$apply.length)
            return;

        const dirty = resolveApplied(collectFilterParams()).toString() !== _lastCommittedParams;
        $apply.toggleClass('btn-primary', dirty);
        $apply.toggleClass('btn-outline-secondary', !dirty);
    }

    /** Writes the resolved filter values into the in-memory applied state and refreshes the dashboard data in place. */
    function commitFilters(params: URLSearchParams): void {
        const cleaned = resolveApplied(params);
        const state = cleaned.toString();
        if (state === _lastCommittedParams)
            return;

        _lastCommittedParams = state;
        _appliedParams = cleaned;

        // Shareable deep link: the committed values land in the URL so copying the address
        // (or reloading) reproduces the filtered view.
        writeUrlParams();

        // Full refresh: publicView refetches every widget with the new applied filter values.
        // resetTablePaging restarts server-paged tables from their first window/page.
        distill.eventBus.publish(distill.EVENTS.DATA_REFRESH_REQUESTED, { resetTablePaging: true });
    }

    interface IDateRangeValue {
        from: string | null;
        to: string | null;
    }

    /**
     * Maps the applied dates back to a preset. The applied state never carries the preset
     * keyword, so we compare the dates against each preset's currently resolved range; anything
     * that doesn't match (including relative presets reopened on a later day) falls back to Custom Range.
     */
    function inferDatePreset(fromValue: string, toValue: string): string {
        if (fromValue === EMPTY_VALUE && toValue === EMPTY_VALUE)
            return DATE_PRESET_ALL;

        for (const preset of INFERABLE_DATE_PRESETS) {
            const range = resolveDatePreset(preset);
            if ((range.from || EMPTY_VALUE) === fromValue && (range.to || EMPTY_VALUE) === toValue)
                return preset;
        }

        return DATE_PRESET_CUSTOM;
    }

    /** Resolves a preset to explicit yyyy-MM-dd dates in UTC — matching the server's UTC-day range semantics (and the verbatim UTC display). */
    function resolveDatePreset(preset: string): IDateRangeValue {
        const now = new Date();
        const fmt = (utcMs: number) => {
            const d = new Date(utcMs);
            return `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`;
        };
        const DAY_MS = 24 * 60 * 60 * 1000;
        const today = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());

        switch (preset) {
            case 'Today':
                return { from: fmt(today), to: fmt(today) };
            case 'Last7Days':
                return { from: fmt(today - 7 * DAY_MS), to: fmt(today) };
            case 'ThisMonth':
                return { from: fmt(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1)), to: fmt(today) };
            case 'LastMonth':
                return { from: fmt(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 1)), to: fmt(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 0)) };
            case 'Last6Months':
                return { from: fmt(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 5, 1)), to: fmt(today) };
            case 'Last12Months':
                return { from: fmt(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 11, 1)), to: fmt(today) };
            default:
                return { from: null, to: null };
        }
    }

    function getDatePresetOptions(): { value: string; label: string }[] {
        return [
            { value: 'All', label: T('All') },
            { value: 'Today', label: T('Today') },
            { value: 'Last7Days', label: T('Last 7 Days') },
            { value: 'ThisMonth', label: T('This Month') },
            { value: 'LastMonth', label: T('Last Month') },
            { value: 'Last6Months', label: T('Last 6 Months') },
            { value: 'Last12Months', label: T('Last 12 Months') },
            { value: 'CustomRange', label: T('Custom Range') }
        ];
    }

    function pad2(value: number): string {
        return value < 10 ? `0${value}` : value.toString();
    }
}
